import {
  createContext,
  useContext,
  useLayoutEffect,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import type { LucideIcon } from "lucide-react";
import { useInlineFrameEmbed } from "./inline-frame-embed-context";
import { useOpenedFrame } from "./use-opened-frame";

export type FrameChromeMenuItem = {
  id: string;
  label: string;
  icon?: LucideIcon;
  onSelect: () => void;
  destructive?: boolean;
};

export type FrameChromeTool =
  | {
      kind: "button";
      id: string;
      label: string;
      /** Names the button where its shown label is terse ("Review 3"). */
      ariaLabel?: string;
      /** Drawn instead of the label, which then names the button. */
      icon?: LucideIcon;
      onSelect: () => void;
      pressed?: boolean;
      disabled?: boolean;
    }
  | {
      kind: "menu";
      id: string;
      label: string;
      icon: LucideIcon;
      items: FrameChromeMenuItem[];
    }
  | {
      kind: "popover";
      id: string;
      label: string;
      icon: LucideIcon;
      renderPopover: (close: () => void) => ReactNode;
    }
  | {
      /** Muted, truncated text that is not a control (a PDF's name). */
      kind: "label";
      id: string;
      label: string;
    }
  | {
      /** The frame's own control (a GitHub branch picker); it reports
       *  whether its menu is open so the pill stays shown meanwhile. */
      kind: "custom";
      id: string;
      render: (onOpenChange: (open: boolean) => void) => ReactNode;
    };

export type FrameChromeConfig = {
  /** An opened window's type tools, before the pill's hairline (at most 3). */
  tools: FrameChromeTool[];
  /** Type extras leading an opened window's ⋯ and right-click menu. */
  menuItems?: FrameChromeMenuItem[];
  titleHint?: string;
  /** Runtime-owned error: displayed by chrome, never cleared by shared Dismiss. */
  error?: string;
};

const EMPTY_CONFIG: FrameChromeConfig = { tools: [] };

/** No ⋯ extras: the shared empty list, stable across renders. */
export const NO_MENU_EXTRAS: readonly FrameChromeMenuItem[] = [];
const ConfigContext = createContext(EMPTY_CONFIG);
const RegisterContext = createContext<Dispatch<
  SetStateAction<FrameChromeConfig>
> | null>(null);

/**
 * Holds one frame's chrome config: the title hint and error every surface's
 * chrome shows, the tools an opened frame's window hosts in its hover pill
 * (`useOpenedFrame`) and the extras its ⋯ and right-click menus lead with.
 */
export function FrameChromeConfigProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [config, setConfig] = useState(EMPTY_CONFIG);
  return (
    <RegisterContext.Provider value={setConfig}>
      <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>
    </RegisterContext.Provider>
  );
}

/**
 * Register a memoized frame config. Returns whether the frame is opened, so
 * its window's hover pill hosts the tools and the frame draws its in-frame
 * controls only when it is not. An inline copy in a conversation reply
 * registers nothing: the chrome around it is the conversation's. Detached
 * previews without a provider are safe (not hosted).
 */
export function useFrameChromeConfig(config: FrameChromeConfig): boolean {
  const provided = useContext(RegisterContext);
  const register = useInlineFrameEmbed() ? null : provided;
  useLayoutEffect(() => {
    register?.(config);
    return () =>
      register?.((current) => (current === config ? EMPTY_CONFIG : current));
  }, [config, register]);
  return useOpenedFrame();
}

export function useFrameChromeConfigValue(): FrameChromeConfig {
  return useContext(ConfigContext);
}
